import axios, { AxiosError } from "axios";
import { SERVER_URL } from "Constants";
import useAsync from "Hooks/useAsync";
import { store } from "Models/Store";
import React, { createContext, useContext, useEffect } from "react";
import alertToast from "Util/ToastifyUtils";
import { Button, Colors, Display, Sizes } from "Views/Components/Button/Button";
import { ButtonGroup } from "Views/Components/Button/ButtonGroup";
import Modal from "Views/Components/Modal/Modal";
import { ESignDocumentPreFillStage } from "./CreateEnvelopeSections/ESignDocumentPreFillSection";
import { ESignRecipientStage } from "./CreateEnvelopeSections/ESignRecipientSection";
import { ESignSenderInformationStage } from "./CreateEnvelopeSections/ESignSenderInformationSection";

export type BusinessEntityESignTemplate = {
	id: string;
	templateContentId: string;
	name: string;
	created: Date;
	lifeSpan: number;
	reminderFrequency: number;
	numberOfSignee: number;
	requireWitness: boolean;
	requireSmsAuth: boolean;
	version: number;
	templateContent: {
		inputFields: string[];
		preFillFieldsForSignee: {[key: string]: string[]}
	}
}

export const getESignTemplatesByBusinessEntityId = async (businessEntityId: string): Promise<BusinessEntityESignTemplate[]> => {
	try {
		const response = await axios.get<BusinessEntityESignTemplate[]>(`${SERVER_URL}/api/esign/business-entity/${businessEntityId}/template`);
		const data = response.data;
		return data;
	} catch (error) {
		throw error as AxiosError;
	}
}

export type RecipientDetail = {
	name: string;
	email: string;
	phone: string;
	preFillValues: {[key: string]: string}
}

type ESignEnvelopeCreateStage = "sender-information" | "common-fields" | "recipients" | "submit";
const eSignEnvelopeCreateStageTitle: { [key in ESignEnvelopeCreateStage]: string } = {
	"common-fields": "Pre-filled Fields",
	"recipients": "Recipients",
	"sender-information": "Your details",
	"submit": "Submit"
}

export type ESignEnvelopeCreateRequest = {
	senderName: string;
	senderCompany: string;
	notificationEmail: string;
	templateId: string;
	preFillValues: { [key: string]: string };
	signees: RecipientDetail[];
}

type ESignEnvelopeCreateState = {
	stage: ESignEnvelopeCreateStage,
	validation: { [key in ESignEnvelopeCreateStage]: boolean };
	businessEntityId: string;
	availableTemplates: BusinessEntityESignTemplate[];
	data: ESignEnvelopeCreateRequest;
}

const initialESignEnvelopeCreateState: ESignEnvelopeCreateState = {
	stage: "sender-information",
	validation: {
		"sender-information": false,
		"common-fields": false,
		"recipients": false,
		"submit": false
	},
	businessEntityId: "",
	availableTemplates: [],
	data: {
		senderName: store.userDisplayName,
		senderCompany: "",
		notificationEmail: "",
		templateId: "",
		preFillValues: {},
		signees: []
	}
}

type PayloadAction =
	| { type: "SetInitialDetail", payload: { businessEntityId: string; businessEntityName: string; availableTemplates: BusinessEntityESignTemplate[] } }
	| { type: "SetStage", payload: { stage: ESignEnvelopeCreateStage } }
	| { type: "SetSenderName", payload: { senderName: string } }
	| { type: "SetSenderCompany", payload: { senderCompany: string } }
	| { type: "SetNotificationEmail", payload: { notificationEmail: string } }
	| { type: "SetRequestData", payload: { templateId: string; prefillValues: { [key: string]: string }; recipients: RecipientDetail[] } }
	| { type: "SetPrefillValues", payload: { prefillValues: { [key: string]: string } } }
	| { type: "SetRecipients", payload: { signees: RecipientDetail[] } }
	| { type: "StageValidation", payload: { stage: ESignEnvelopeCreateStage, isValid: boolean } };

export const ESignEnvelopeCreateContext = createContext<ESignEnvelopeCreateState>(initialESignEnvelopeCreateState);
export const ESignEnvelopeCreateDispatchContext = createContext<React.Dispatch<PayloadAction>>(() => { });

function createEnvelopeReducer(state: ESignEnvelopeCreateState, action: PayloadAction): ESignEnvelopeCreateState {
	switch (action.type) {
		case "SetInitialDetail":
			return {
				...state,
				businessEntityId: action.payload.businessEntityId,
				availableTemplates: action.payload.availableTemplates,
				data: {
					...state.data,
					senderCompany: action.payload.businessEntityName,
					templateId: "",
					preFillValues: {},
					signees: []
				},
			};
		case "SetRequestData":
			return { ...state, data: {...state.data, templateId: action.payload.templateId, preFillValues: action.payload.prefillValues, signees: action.payload.recipients} };
		case "SetStage":
			return { ...state, stage: action.payload.stage };
		case "SetRecipients":
			return { ...state, data: { ...state.data, signees: action.payload.signees } };
		case "SetPrefillValues":
			return { ...state, data: { ...state.data, preFillValues: action.payload.prefillValues } };
		case "SetSenderName":
			return { ...state, data: { ...state.data, senderName: action.payload.senderName } };
		case "SetSenderCompany":
			return { ...state, data: { ...state.data, senderCompany: action.payload.senderCompany } };
		case "SetNotificationEmail":
			return { ...state, data: { ...state.data, notificationEmail: action.payload.notificationEmail } };
		case "StageValidation":
			return { ...state, validation: { ...state.validation, [action.payload.stage]: action.payload.isValid } };
		default:
			return state;
	}
}

type ESignEnvelopeCreateProviderProps = {
	businessEntityId: string;
	businessEntityName: string;
	children: React.ReactNode;
}

export const ESignEnvelopeCreateProvider: React.FunctionComponent<ESignEnvelopeCreateProviderProps> = ({businessEntityId, businessEntityName, children }) => {
	const [eSignEnvelopeCreateState, dispatch] = React.useReducer(createEnvelopeReducer, initialESignEnvelopeCreateState);

	const { data, type } = useAsync<BusinessEntityESignTemplate[]>(
		async () => (await getESignTemplatesByBusinessEntityId(businessEntityId)), [businessEntityId]
	);

	useEffect(() => {
		if (type === "data" && data && data.length > 0) {
			dispatch({
				type: "SetInitialDetail",
				payload: {
					businessEntityId: businessEntityId,
					businessEntityName: businessEntityName,
					availableTemplates: data,
				}
			});
		}
	}, [type, data, businessEntityId, businessEntityName]);

	return (
		<ESignEnvelopeCreateContext.Provider value={eSignEnvelopeCreateState}>
			<ESignEnvelopeCreateDispatchContext.Provider value={dispatch}>
				{children}
			</ESignEnvelopeCreateDispatchContext.Provider>
		</ESignEnvelopeCreateContext.Provider>
	)
}


export const ESignEnvelopeCreateWorkflow: React.FunctionComponent<{ onClose: () => void }> = ({ onClose }) => {
	const { stage } = useContext(ESignEnvelopeCreateContext);
	return (
		<Modal isOpen label="Create ESign Envelope" className="mvs-modal-content">
			<div className="modal__header">
				<h4><b>{eSignEnvelopeCreateStageTitle[stage]}</b></h4>
				<Button
					className="modal-close"
					key="header-cancel"
					icon={{ icon: 'cross', iconPos: 'icon-only' }}
					display={Display.Text}
					onClick={onClose}
					sizes={Sizes.Medium}
					colors={Colors.Error}
					labelVisible={false}
				>
					Cancel
				</Button>
			</div>
			{stage === "sender-information" && <ESignSenderInformationStage />}
			{stage === "recipients" && <ESignRecipientStage />}
			{stage === "common-fields" && <ESignDocumentPreFillStage />}
			<ESignEnvelopeCreateFooter onClose={onClose} />
		</Modal>
	)
}

export const ESignEnvelopeCreateFooter: React.FunctionComponent<{ onClose: () => void }> = ({ onClose }) => { 
	const { stage, data, validation } = useContext(ESignEnvelopeCreateContext);
	const dispatch = useContext(ESignEnvelopeCreateDispatchContext);
	const hasCommonFields = Object.keys(data.preFillValues).length > 0;

	const handlePrev = () => {
		switch (stage) {
			case "sender-information":
				onClose();
				break;
			case "common-fields":
				dispatch({ type: "SetStage", payload: { stage: "sender-information" } });
				break;
			case "recipients":
				dispatch({ type: "SetStage", payload: { stage: hasCommonFields ? "common-fields" : "sender-information" } });
				break;
			default:
				console.warn("Unexpected stage in ESignEnvelopeCreateFooter:", stage);
				break;
		}
	}

	const handleNext = () => {
		switch (stage) {
			case "sender-information":
				dispatch({ type: "SetStage", payload: { stage: hasCommonFields ? "common-fields" : "recipients" } });
				break;
			case "common-fields":
				dispatch({ type: "SetStage", payload: { stage: "recipients" } });
				break;
			case "recipients":
				handleSubmit();
				break;
			default:
				console.warn("Unexpected stage in ESignEnvelopeCreateFooter:", stage);
				onClose();
		}
	}

	const handleSubmit = async () => {
		try {
			await axios.post(`${SERVER_URL}/api/esign/initiate`, data);
			alertToast("Successfully sent the E-Sign Invitation", "success");
			onClose();
		} catch (error) {
			console.error(error);
			alertToast("Failed to send the E-Sign Invitation", "error");
		}
	}

	const isNextDisabled = () => {
		switch (stage) {
			case "sender-information":
				return !validation["sender-information"];
			case "common-fields":
				return !validation["common-fields"];
			case "recipients":
				return Object.keys(data.signees).length === 0 || !validation["recipients"];
			default:
				return false;
		}
	}

	return (
		<ButtonGroup className="modal__actions">
			<Button
				display={Display.Text}
				colors={Colors.Error}
				sizes={Sizes.Medium}
				onClick={handlePrev}
			>
				{stage === "sender-information" ? "Cancel" : "Back"}
			</Button>
			<Button
				display={Display.Solid}
				colors={Colors.Primary}
				sizes={Sizes.Medium}
				onClick={handleNext}
				disabled={isNextDisabled()}
			>
				{stage === "recipients" ? "Send" : "Next"}
			</Button>
		</ButtonGroup>
	);
}